/**
 * Client-side search utilities using Lunr
 */

import lunr from 'lunr';

export interface SearchDocument {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  tags: string[];
  slug: string;
  date: string;
}

export interface SearchResult extends SearchDocument {
  score: number;
}

interface SearchIndexData {
  index: Record<string, any>; // Serialized Lunr index structure
  documents: SearchDocument[];
}

let searchIndex: lunr.Index | null = null;
let searchDocuments: SearchDocument[] = [];

/**
 * Load the search index from the static JSON file
 */
export async function loadSearchIndex(): Promise<void> {
  if (searchIndex) {
    return; // Already loaded
  }

  try {
    const response = await fetch('/search-index.json');
    const data: SearchIndexData = await response.json();
    
    // Load the serialized Lunr index
    // Type assertion needed as Lunr's load expects a specific serialized format
    searchIndex = lunr.Index.load(data.index);
    searchDocuments = data.documents;
  } catch (error) {
    console.error('Failed to load search index:', error);
    throw error;
  }
}

/**
 * Search the index for the given query
 */
export function search(query: string): SearchResult[] {
  if (!searchIndex || !query.trim()) {
    return [];
  }

  try {
    // Perform the search
    const results = searchIndex.search(query);

    // Create a Map for O(1) document lookup
    const docMap = new Map(searchDocuments.map((d) => [d.id, d]));

    // Map results to documents with scores
    return results.map((result) => {
      const doc = docMap.get(result.ref);
      if (!doc) {
        return null;
      }
      return {
        ...doc,
        score: result.score,
      };
    }).filter((result): result is SearchResult => result !== null);
  } catch (error) {
    console.error('Search error:', error);
    return [];
  }
}

/**
 * Check if search index is loaded
 */
export function isSearchIndexLoaded(): boolean {
  return searchIndex !== null;
}
